import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, Sparkles, User, LogOut, Home, Shield, Map, PlusCircle, ShieldAlert, MessageSquareHeart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import gsap from 'gsap';

const Sidebar = ({ onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, revokeAllSessions } = useAuth();
  const aiIconRef = useRef(null);

  // Direct navigation to all essential pages
  const menuItems = [
    { path: '/', label: 'Home', icon: Home, mobileOnly: true },
    { path: '/dashboard', label: 'Dashboard', icon: LayoutGrid },
    { path: '/dashboard/ai-planner', label: 'AI Trip Planner', icon: Sparkles, isPremium: true },
    { path: '/dashboard/create-trip', label: 'Create Trip', icon: PlusCircle },
    { path: '/dashboard/my-trips', label: 'My Trips', icon: Map },
    { path: '/dashboard/feedback', label: 'Trip Feedback', icon: MessageSquareHeart },
    { path: '/dashboard/profile', label: 'Profile & Settings', icon: User },
  ];

  // Dynamic profile with company logo fallback
  const travelProfile = {
    name: user?.name || localStorage.getItem('userName') || 'Explorer',
    email: user?.email || localStorage.getItem('userEmail') || 'traveler@syncandexplore.com',
    role: user?.role || localStorage.getItem('userRole') || 'Traveller',
    avatarUrl: user?.avatarUrl || null,
  };

  const isAdmin = travelProfile?.role === 'admin' || travelProfile?.role === 'superAdmin' || localStorage.getItem('userRole') === 'admin' || localStorage.getItem('userRole') === 'superAdmin';

  // Animate AI Planner icon on mount
  useEffect(() => {
    if (aiIconRef.current) {
      gsap.to(aiIconRef.current, {
        rotation: 360,
        duration: 4,
        repeat: -1,
        ease: "none"
      });
    }
  }, []);

  const handleLinkClick = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleLogout = async () => {
    const confirmLogout = window.confirm('Are you sure you want to log out?');
    if (confirmLogout) {
      try {
        await logout();
        navigate('/login', { replace: true });
      } catch (error) {
        console.error('Logout failed:', error);
        navigate('/login', { replace: true });
      }
    }
  };

  const handleLogoutAllDevices = async () => {
    const confirmLogout = window.confirm(
      'Are you sure you want to log out from all devices? This will invalidate all your active sessions across all browsers and devices.'
    );
    if (confirmLogout) {
      try {
        await revokeAllSessions();
        navigate('/login', { replace: true });
      } catch (error) {
        console.error('Logout all devices failed:', error);
        navigate('/login', { replace: true });
      }
    }
  };

  const avatarSrc = travelProfile?.avatarUrl || "/images/sync_logo.png";

  return (
    <div className="w-[280px] h-full lg:h-[calc(100vh-40px)] bg-white lg:rounded-[40px] flex flex-col overflow-hidden border-r lg:border-none border-gray-100 shadow-sm">
      {/* User Header / Profile Quick Link */}
      <div className="p-4 lg:p-6 flex-shrink-0">
        <Link
          to="/dashboard/profile"
          onClick={handleLinkClick}
          className="flex items-center gap-3 p-2 -m-2 rounded-2xl hover:bg-gray-50 transition-colors group cursor-pointer"
        >
          <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center border border-gray-200 flex-shrink-0">
            <img
              src={avatarSrc}
              alt={travelProfile?.name || "User Avatar"}
              onError={(e) => {
                e.target.src = "/images/sync_logo.png";
              }}
              className={`w-full h-full ${travelProfile?.avatarUrl ? 'object-cover' : 'object-contain p-1'}`}
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 text-sm lg:text-base truncate group-hover:text-[#2A9D8F] transition-colors">
              {travelProfile?.name || 'Explorer'}
            </h3>
            <p className={`text-xs font-semibold truncate ${isAdmin ? 'text-[#2A9D8F]' : 'text-gray-400'}`}>
              {isAdmin ? 'Administrator' : 'Traveller'}
            </p>
          </div>
        </Link>
      </div>

      {/* Navigation Links */}
      <nav className="px-2 lg:px-4 flex-1 min-h-0 overflow-y-auto custom-scroll" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
        <ul className="space-y-1.5">
          {isAdmin && (
            <li key="/admin" className="mb-2">
              <Link
                to="/admin"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 lg:px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#2A9D8F] to-emerald-600 text-white font-bold shadow-md hover:shadow-lg transition-all duration-200"
              >
                <Shield className="w-[16px] h-[16px] lg:w-[18px] lg:h-[18px]" />
                <span className="text-[14px] lg:text-[15px]">Admin Dashboard</span>
              </Link>
            </li>
          )}
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const isPremium = item.isPremium;
            
            let visibilityClass = '';
            if (item.mobileOnly) {
              visibilityClass = 'lg:hidden';
            } else if (item.desktopOnly) {
              visibilityClass = 'hidden lg:block';
            }
            
            if (isPremium) {
              return (
                <li key={`${item.path}-${item.label}`} className={visibilityClass}>
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className={`flex items-center gap-3 px-3 lg:px-4 py-2.5 rounded-xl transition-all duration-200 ${
                      isActive
                        ? "bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-500 text-white font-semibold shadow-lg shadow-amber-500/30"
                        : "text-gray-600 hover:bg-[#2A9D8F]/10 hover:text-[#2A9D8F]"
                    }`}
                  >
                    <div ref={aiIconRef} className="transition-transform duration-200">
                      <Icon className="w-[16px] h-[16px] lg:w-[18px] lg:h-[18px]" />
                    </div>
                    <span className="text-[14px] lg:text-[15px] font-medium">
                      {item.label}
                    </span>
                  </Link>
                </li>
              );
            }
            
            return (
              <li key={`${item.path}-${item.label}`} className={visibilityClass}>
                <Link
                  to={item.path}
                  onClick={handleLinkClick}
                  className={`flex items-center gap-3 px-3 lg:px-4 py-2.5 rounded-xl transition-all duration-200 ${
                    isActive
                      ? "bg-[#2A9D8F] text-white font-semibold shadow-sm"
                      : "text-gray-600 hover:bg-[#2A9D8F]/10 hover:text-[#2A9D8F]"
                  }`}
                >
                  <Icon
                    className={`w-[16px] h-[16px] lg:w-[18px] lg:h-[18px] transition-transform duration-200 ${
                      isActive ? "scale-110" : ""
                    }`}
                  />
                  <span className="text-[14px] lg:text-[15px] font-medium">
                    {item.label}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Logout Footer */}
      <div className="p-2 pb-3 lg:p-4 flex-shrink-0 border-t border-gray-100 bg-white lg:rounded-b-[40px] space-y-1">
        <button 
          onClick={handleLogoutAllDevices}
          className="flex items-center gap-3 w-full px-3 lg:px-4 py-2 text-left text-amber-700 hover:bg-amber-50 hover:text-amber-800 rounded-xl transition-all duration-200 border border-transparent hover:border-amber-200 cursor-pointer"
          title="Log out from all connected browsers and devices"
        >
          <ShieldAlert className="w-[16px] h-[16px] lg:w-[18px] lg:h-[18px] text-amber-600 flex-shrink-0" />
          <span className="text-[13px] lg:text-[14px] font-medium">Log Out All Devices</span>
        </button>

        <button 
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 lg:px-4 py-2 text-left text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl transition-all duration-200 border border-transparent hover:border-red-200 cursor-pointer"
        >
          <LogOut className="w-[16px] h-[16px] lg:w-[18px] lg:h-[18px] flex-shrink-0" />
          <span className="text-[13px] lg:text-[14px] font-medium">Log Out</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
